import { useCallback, useEffect, useState } from 'react';
import { authAdmin, type AuthUser } from '../auth';
import { useToast } from '../lib/toast';
import { Modal, Field, inputCls } from '../lib/Modal';
import { formatDateTime } from '../lib/format';

export default function UsersPage() {
  const { push } = useToast();
  const [data, setData] = useState<AuthUser[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [viewing, setViewing] = useState<AuthUser | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await authAdmin.listUsers({ page, limit: 50, search: search || undefined, role: roleFilter || undefined, surface: 'admin.onlytrade.ai' });
      setData(r.data); setTotal(r.total); setTotalPages(r.totalPages);
    } catch (e) { setErr((e as Error).message); }
  }, [page, search, roleFilter]);

  useEffect(() => { void load(); }, [load]);

  async function sendLink(u: AuthUser) {
    try {
      await authAdmin.sendMagicLink(u.id, 'admin.onlytrade.ai');
      push(`Magic link sent to ${u.email}`, 'success');
    } catch (e) { push('Failed: ' + (e as Error).message, 'error'); }
  }

  async function toggleStatus(u: AuthUser) {
    const action = u.is_active ? 'deactivate' : 'reactivate';
    if (!confirm(`${action[0].toUpperCase() + action.slice(1)} ${u.email}? ${u.is_active ? 'All their sessions will end immediately.' : ''}`)) return;
    try {
      await authAdmin.setStatus(u.id, !u.is_active);
      push(u.is_active ? 'Deactivated' : 'Reactivated', 'success');
      void load();
    } catch (e) { push('Failed: ' + (e as Error).message, 'error'); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Users</h1>
        <button onClick={() => setShowInvite(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded">+ Invite User</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <input placeholder="Search email or name" value={search}
               onChange={e => { setSearch(e.target.value); setPage(1); }}
               className="flex-1 bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600" />
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
                className="bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600">
          <option value="">All roles</option>
          <option value="super_admin">super_admin</option>
          <option value="onlytrade_admin">onlytrade_admin</option>
          <option value="kommercio_admin">kommercio_admin</option>
        </select>
        <div className="flex items-center text-sm text-ink-muted">{total} total</div>
      </div>

      {err && <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">{err}</div>}
      {data === null && !err && <div className="text-ink-muted text-sm">Loading…</div>}
      {data && data.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          No users. Invite the first one.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-bg border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Email</th>
                <th className="text-left px-4 py-2 font-semibold">Role</th>
                <th className="text-left px-4 py-2 font-semibold">Devices</th>
                <th className="text-left px-4 py-2 font-semibold">Last login</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(u => (
                <tr key={u.id} className="border-b border-ink-border last:border-0 hover:bg-ink-hover/30">
                  <td className="px-4 py-2">
                    <div className="font-medium">{u.email}</div>
                    {u.display_name && <div className="text-xs text-ink-muted">{u.display_name}</div>}
                  </td>
                  <td className="px-4 py-2"><span className="text-xs bg-ink-border px-2 py-0.5 rounded">{u.role}</span></td>
                  <td className="px-4 py-2 tabular-nums">{u.credential_count}</td>
                  <td className="px-4 py-2 text-ink-muted">{u.last_login_at ? formatDateTime(u.last_login_at) : '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      u.is_active ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300'
                    }`}>{u.is_active ? 'active' : 'deactivated'}</span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setViewing(u)}
                            className="text-xs bg-ink-border hover:bg-ink-border/70 text-ink-text px-2.5 py-1 rounded mr-1">View</button>
                    <button onClick={() => sendLink(u)}
                            className="text-xs bg-brand-600/80 hover:bg-brand-600 text-white px-2.5 py-1 rounded mr-1">Send link</button>
                    <button onClick={() => toggleStatus(u)}
                            className={`text-xs px-2.5 py-1 rounded text-white ${u.is_active ? 'bg-state-danger/80 hover:bg-state-danger' : 'bg-green-800 hover:bg-green-700'}`}>
                      {u.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
                    className={`px-3 py-1 text-xs rounded ${
                      p === page ? 'bg-brand-600 text-white' : 'bg-ink-card text-ink-text hover:bg-ink-hover border border-ink-border'
                    }`}>{p}</button>
          ))}
        </div>
      )}

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); void load(); }} />}
      {viewing && <UserDetail user={viewing} onClose={() => setViewing(null)} onChange={load} />}
    </div>
  );
}

function InviteModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { push } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('onlytrade_admin');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await authAdmin.invite({ email: email.trim(), role, surface: 'admin.onlytrade.ai', display_name: displayName.trim() || undefined });
      push(`Invite sent to ${email}`, 'success');
      onSaved();
    } catch (e) { push('Invite failed: ' + (e as Error).message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Modal open={true} title="Invite User" onClose={onClose}>
      <form onSubmit={save}>
        <Field label="Email"><input required type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} /></Field>
        <Field label="Display name" hint="optional"><input className={inputCls} value={displayName} onChange={e => setDisplayName(e.target.value)} /></Field>
        <Field label="Role">
          <select className={inputCls} value={role} onChange={e => setRole(e.target.value)}>
            <option value="onlytrade_admin">onlytrade_admin</option>
            <option value="super_admin">super_admin</option>
          </select>
        </Field>
        <div className="text-xs text-ink-muted mb-3">
          They'll receive an email with a sign-in link. First click registers their passkey on their device.
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={busy} className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded">
            {busy ? 'Inviting…' : 'Send Invite'}
          </button>
          <button type="button" onClick={onClose} className="bg-ink-border hover:bg-ink-border/70 text-ink-text text-sm px-4 py-2 rounded">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function UserDetail({ user, onClose, onChange }: { user: AuthUser; onClose: () => void; onChange: () => void }) {
  const { push } = useToast();
  const [creds, setCreds] = useState<Awaited<ReturnType<typeof authAdmin.listCredentials>>['data'] | null>(null);

  const load = useCallback(async () => {
    try { setCreds((await authAdmin.listCredentials(user.id)).data); }
    catch (e) { push('Failed to load devices: ' + (e as Error).message, 'error'); }
  }, [user.id, push]);

  useEffect(() => { void load(); }, [load]);

  async function revoke(id: string) {
    if (!confirm('Revoke this device? The user will need to re-register a passkey.')) return;
    try {
      await authAdmin.revokeCredential(id);
      push('Device revoked', 'success');
      void load();
      onChange();
    } catch (e) { push('Revoke failed: ' + (e as Error).message, 'error'); }
  }

  return (
    <Modal open={true} title={user.email} onClose={onClose} width={640}>
      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <div><span className="text-ink-muted">Role:</span> {user.role}</div>
        <div><span className="text-ink-muted">Status:</span> {user.is_active ? 'active' : 'deactivated'}</div>
        <div><span className="text-ink-muted">Surfaces:</span> {user.surfaces.join(', ') || '—'}</div>
        <div><span className="text-ink-muted">Last login:</span> {user.last_login_at ? formatDateTime(user.last_login_at) : '—'}</div>
      </div>

      <h3 className="text-xs uppercase text-ink-muted font-semibold mb-2 tracking-wider">Registered devices</h3>
      {creds === null && <div className="text-sm text-ink-muted">Loading…</div>}
      {creds && creds.length === 0 && <div className="text-sm text-ink-muted bg-ink-bg border border-ink-border rounded p-3">No devices yet — user hasn't registered a passkey.</div>}
      {creds && creds.length > 0 && (
        <div className="bg-ink-bg border border-ink-border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-card border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr><th className="text-left px-3 py-2">Device</th><th className="text-left px-3 py-2">RP</th><th className="text-left px-3 py-2">Last used</th><th className="text-right px-3 py-2">Actions</th></tr>
            </thead>
            <tbody>
              {creds.map(c => (
                <tr key={c.id} className="border-b border-ink-border last:border-0">
                  <td className="px-3 py-2 font-medium">{c.device_name || '—'}</td>
                  <td className="px-3 py-2 text-ink-muted font-mono text-xs">{c.rp_id}</td>
                  <td className="px-3 py-2 text-ink-muted">{c.last_used_at ? formatDateTime(c.last_used_at) : 'never'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => revoke(c.id)} className="text-xs bg-state-danger/80 hover:bg-state-danger text-white px-2 py-1 rounded">Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
